// `GET /api/auth/session` — the one request that answers "who is this".
//
// §05: no token ever reaches the browser. The session is an opaque, revocable
// cookie the API exchanges for Keycloak tokens it holds server-side, so this
// app sends nothing and asks the API instead. That is why 401 here is a NORMAL
// answer meaning "nobody is signed in" rather than a failure to report: the
// endpoint is public, and an anonymous visitor asking who they are is not an
// error, it is the question.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { SessionInfo } from "@crm/shared";
import { apiGet } from "../api/client.js";
import { isApiProblem } from "../api/problem.js";

export const SESSION_QUERY_KEY = ["auth", "session"] as const;

/** `null` is "signed out" — a resolved answer, not an error. */
export type SessionResult = SessionInfo | null;

export function useSessionQuery(enabled: boolean): UseQueryResult<SessionResult> {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    enabled,
    queryFn: async ({ signal }): Promise<SessionResult> => {
      try {
        return await apiGet(SessionInfo, "/auth/session", { signal });
      } catch (error) {
        if (isApiProblem(error) && error.status === 401) return null;
        throw error;
      }
    },
    // Signed-in-ness is not something to serve from a 30-second-stale cache
    // after a 401 has already told us it changed.
    staleTime: 0,
    retry: false,
  });
}

/**
 * `GET /api/auth/session/login?return_to=<path>` is a BROWSER NAVIGATION, not a
 * fetch: it ends in a 302 to Keycloak, and an XHR would follow that redirect
 * into a CORS wall instead of showing the user a login page. So this returns a
 * URL for an `<a href>` / `location.assign`, and nothing here ever calls it.
 *
 * The path and the parameter both belong to the BFF that now fronts this app
 * (docs/crm-kotlin-port.md §3.4): it owns the session, so login is one of ITS
 * endpoints — `/api/auth/session/…`, taking `return_to` — not the
 * `/api/auth/login?redirect=` the retired TypeScript API served. Nothing but
 * the two constants in this file had to move: the session probe, the shape of
 * `SessionInfo`, the 401 listener and the loop-breaker are all unchanged.
 */
export function loginUrl(redirectTo: string): string {
  return `/api/auth/session/login?return_to=${encodeURIComponent(redirectTo)}`;
}

/**
 * Signing out is the same kind of thing as signing in, and for the same reason:
 * a navigation, not a fetch.
 *
 * This used to be a `POST` whose result the app acted on, and it did not work.
 * Ending the CRM's own session is only half a sign-out — the other half is
 * Keycloak's SSO cookie, which lives in the BROWSER, so nothing the app does
 * over XHR can reach it. A local-only sign-out left that cookie in place, and
 * because every signed-out visitor is sent straight back to the login endpoint
 * (see `autoSignIn` — deliberate), Keycloak answered with a silent re-login:
 * same user, same page, under a second. It looked exactly like a dead button.
 *
 * So the endpoint 302s and this is just a URL to put in an `href`. The
 * server-side session, the cookie and the Keycloak session are all ended by
 * following it, and nothing here has to sequence any of that — which is why
 * there is no mutation, no cache sweep and no error state to render. The
 * sibling BFF does the same (`GET /api/auth/session/logout`).
 *
 * The cache needs no sweeping because the document does not survive: a full
 * navigation discards every query the previous user could read.
 *
 * `return_to=/` is explicit rather than left to a default: the BFF sends the
 * browser to the app ROOT after Keycloak ends its session, and that root is a
 * separately-allowed post-logout URI on the Keycloak client — a callback path
 * reached with no authorization code would be an error document, not a page.
 */
export const LOGOUT_URL = "/api/auth/session/logout?return_to=/";

/** Where to come back to after signing in: the page the user was actually on. */
export function currentPath(): string {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname}${window.location.search}`;
}
